import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Plus, Trash2, ExternalLink, Search } from "lucide-react";

const MODES = [["current", "Follow current guidance"], ["program_version", "Match program version"]];
const TYPES = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];

// Task Template → Knowledge Center references (never a copy of the file).
export function TemplateResources({ value = [], onChange }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!adding) return undefined;
    const id = setTimeout(() => {
      api.get("/knowledge/sources", { params: { q, page_size: 15 } }).then((r) => setRows(r.data.items || r.data.sources || [])).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q, adding]);
  const add = (s) => {
    onChange([...value, { source_id: s.id, content_key: s.content_key || null, title: s.display_title || s.title, mode: "current" }]);
    setAdding(false); setQ("");
  };
  return (
    <div data-testid="tt-resources">
      <label className="text-xs font-medium text-slate-500">Resources</label>
      <div className="space-y-1.5 mt-1">
        {value.map((r, i) => (
          <div key={`${r.source_id}-${i}`} className="rounded-lg border border-slate-200 px-3 py-2 flex items-center gap-2 text-sm" data-testid={`tt-resource-${i}`}>
            <BookOpen className="w-4 h-4 text-brand shrink-0" />
            <div className="flex-1 min-w-0"><p className="truncate text-slate-700">{r.title}</p><p className="text-[11px] text-slate-400">Knowledge Center{r.content_key ? ` · ${r.content_key}` : " · no content key (follows this source only)"}</p></div>
            <Select value={r.mode} onValueChange={(v) => onChange(value.map((x, j) => (j === i ? { ...x, mode: v } : x)))}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid={`tt-resource-mode-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{MODES.map(([k, l]) => <SelectItem key={k} value={k} disabled={k === "program_version" && !r.content_key}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <a href={`/admin/knowledge?source=${r.source_id}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-600" data-testid={`tt-resource-view-${i}`}><ExternalLink className="w-4 h-4" /></a>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`tt-resource-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!adding ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="tt-resource-add"><Plus className="w-3.5 h-3.5 mr-1" />Add Knowledge Base Resource</Button>
        ) : (
          <div className="rounded-lg border border-slate-200 p-2">
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Knowledge Center sources…" className="pl-8" data-testid="tt-resource-search" /></div>
            <div className="max-h-48 overflow-y-auto mt-1 divide-y" data-testid="tt-resource-results">
              {rows.length === 0 ? <p className="text-xs text-slate-400 p-2">No matching sources.</p> : rows.map((s) => (
                <button key={s.id} type="button" onClick={() => add(s)} className="w-full text-left px-2 py-1.5 hover:bg-slate-50" data-testid={`tt-resource-pick-${s.id}`}>
                  <p className="text-sm text-slate-700">{s.display_title || s.title}</p>
                  <p className="text-[11px] text-slate-400">{[s.category, (s.applicable_programs || []).join("/"), (s.applicable_states || []).join(", ") || "All states", s.program_version || s.document_year, s.status, s.scope_type].filter(Boolean).join(" · ")}{s.confidential_warning ? " · CONFIDENTIAL (never shown to candidates)" : ""}</p>
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setAdding(false)} className="text-xs text-slate-400 mt-1">Cancel</button>
          </div>
        )}
        <p className="text-[11px] text-slate-400">Candidates only see published, candidate-audience, non-confidential sources.</p>
      </div>
    </div>
  );
}

export function TemplateSubmission({ value, onChange }) {
  const v = value || { required: false };
  const set = (p) => onChange({ ...v, ...p });
  const types = v.file_types || ["pdf", "jpg", "jpeg", "png"];
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="tt-submission">
      <label className="flex items-center justify-between text-sm text-slate-700">Candidate Submission — requires document upload
        <Switch checked={!!v.required} onCheckedChange={(c) => set({ required: c })} data-testid="tt-submission-required" /></label>
      {v.required && (
        <>
          <div className="flex flex-wrap gap-1.5">{TYPES.map((t) => (
            <button key={t} type="button" onClick={() => set({ file_types: types.includes(t) ? types.filter((x) => x !== t) : [...types, t] })}
              className={`text-xs rounded border px-2 py-1 ${types.includes(t) ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-500"}`} data-testid={`tt-submission-type-${t}`}>{t.toUpperCase()}</button>
          ))}</div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-500">Max files</label>
            <Input type="number" min={1} max={10} value={v.max_files || 3} onChange={(e) => set({ max_files: Number(e.target.value) })} className="w-20 h-8" data-testid="tt-submission-max" />
            <label className="flex items-center gap-2 text-xs text-slate-600 ml-auto">Staff review required
              <Switch checked={v.review_required !== false} onCheckedChange={(c) => set({ review_required: c })} data-testid="tt-submission-review" /></label>
          </div>
          <Input value={v.instructions || ""} onChange={(e) => set({ instructions: e.target.value })} placeholder="Upload instructions (optional)" data-testid="tt-submission-instructions" />
        </>
      )}
    </div>
  );
}
